import { useAuth } from '@hourly/contexts/AuthContext';
import {
  Chip,
  ChipSize,
  ChipVariant,
  type Column,
  SearchInput,
  Table,
} from '@hourly-hds/components';
import { useState } from 'react';
import AsyncDisplay from '@shared/components/AsyncDisplay';
import useDebounceCallback from '@shared/data/hooks/useDebounceCallback';
import Page from '../../../_layout/components/Page';
import styles from './Customers.view.module.css';
import { useLiveCustomers } from './data/hooks/useLiveCustomers';
import { Customer, CustomerStatus } from './data/models/customer.model';

const columns: Column<Customer>[] = [
  {
    header: 'customer name',
    accessorKey: 'name',
  },
  {
    header: 'plan',
    accessorKey: 'plan',
  },
  {
    header: 'first payroll run date',
    accessorKey: 'firstPayrollRunDate',
  },
  {
    header: 'account status',
    accessorKey: 'status',
    cell: (info: { getValue: () => CustomerStatus }) => (
      <Chip size={ChipSize.Large} variant={ChipVariant.Success}>
        {info.getValue()}
      </Chip>
    ),
  },
] as Column<Customer>[];

export default function CustomersIndex() {
  const { sessionState } = useAuth();
  const { customersPage, fetchCustomersPage, isGetCustomersSettled } = useLiveCustomers(
    sessionState.partnerId ?? ''
  );
  const [search, setSearch] = useState('');

  const handlePageChange = (newPage: number) => {
    fetchCustomersPage({ page: newPage, search });
  };

  const debouncedSearch = useDebounceCallback((value: string) => {
    fetchCustomersPage({ page: 1, search: value });
  }, 300);

  return (
    <Page>
      <Page.Header>
        <Page.Title>Live Customers</Page.Title>
      </Page.Header>
      <Page.Content>
        <div className={styles.search}>
          <SearchInput
            aria-label={'search customers'}
            onChange={(value) => {
              setSearch(value);
              debouncedSearch(value);
            }}
            value={search}
            placeholder="Search"
          />
        </div>
        <AsyncDisplay isSettled={isGetCustomersSettled} hasData={customersPage.totalItems > 0}>
          <div className={styles.tableContainer}>
            <Table
              data={customersPage.items}
              columns={columns}
              totalRecords={customersPage.totalItems}
              currentPage={customersPage.current}
              onPageChange={handlePageChange}
            />
          </div>
        </AsyncDisplay>
      </Page.Content>
    </Page>
  );
}
